import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Input, Modal, ModalBody, ModalHeader, Spinner, Table, UncontrolledTooltip } from "reactstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { setLoadingOverlay } from "../../store/slices/loadingOverlaySlice";
import NotificationConfigService from "../../services/notificationConfig";
import Breadcrumbs from "../../components/Breadcrumb";
import { CompanyId } from "../../constants";
import LocalstorageService from "../../helpers/localstorage-service";

const ROLE_COLUMNS = [
  { roleId: 1, translationKey: "ADMIN.ROLE_SUPER_ADMIN" },
  { roleId: 2, translationKey: "ADMIN.ROLE_COMPANY_ADMIN" },
  { roleId: 3, translationKey: "ADMIN.ROLE_AGENT" },
  { roleId: 4, translationKey: "ADMIN.ROLE_EMPLOYEE" },
];

// הסברים לכל סוג הודעה — מוצגים כ-tooltip ובחלון עזרה
const MESSAGE_INFO = {
  1: {
    name: "תקלה במסוף",
    description: "שגיאת חומרה שמתקבלת מ-Casponet",
    schedule: "כל 6 דקות, 08:00–22:00",
    throttle: "16 שעות בין התראות",
    shabbat: "נשלח תמיד בשעות פעילות",
  },
  2: {
    name: "מסוף יצא מזמינות",
    description: "מסוף שלא מופיע ברשימת Casponet",
    schedule: "כל 6 דקות",
    throttle: "פעם אחת למסוף",
    shabbat: "נשלח תמיד",
  },
  3: {
    name: "מסוף חדש",
    description: "מסוף חדש שזוהה במערכת Casponet",
    schedule: "כל 6 דקות",
    throttle: "פעם אחת למסוף",
    shabbat: "נשלח תמיד",
  },
  4: {
    name: "שינוי בעלות",
    description: "מסוף שעבר לחברה אחרת",
    schedule: "כל 6 דקות",
    throttle: "פעם אחת למסוף",
    shabbat: "נשלח תמיד",
  },
  5: {
    name: "הפקדות בבנק",
    description: "עדכון על הפקדות שהתקבלו בבנק",
    schedule: "08:45 ו-15:45",
    throttle: "ללא",
    shabbat: "נשלח תמיד",
  },
  6: {
    name: "אצווה חסרה",
    description: "אצווה שלא נמצאה במערכת",
    schedule: "08:45 ו-15:45",
    throttle: "ללא",
    shabbat: "נשלח תמיד",
  },
  7: {
    name: "סוף יום — כסף תקוע",
    description: "מסופים שלא בוצע להם סוף יום ויש בהם כסף. בוקר: כולל הודעה לעובד. ערב: תזכורת למנהלים בלבד.",
    schedule: "08:30 בוקר, 19:00 ערב",
    throttle: "ללא — נשלח בוקר וערב",
    shabbat: "הודעה לעובד לא נשלחת בשבת — נשלחת ביום ראשון",
  },
  8: {
    name: "פער בין טעינות",
    description: "פער בין טעינת מסוף לבין ספירה ידנית",
    schedule: "פעם ביום ב-13:00",
    throttle: "פעם ביום",
    shabbat: "נשלח תמיד",
  },
  9: {
    name: "פער בין תחילת יום לסוף יום",
    description: "חוסר התאמה בין סכום תחילת יום לסוף יום",
    schedule: "פעם ביום",
    throttle: "פעם ביום",
    shabbat: "נשלח תמיד",
  },
  10: {
    name: "חסרה הפקדה",
    description: "הפקדה שלא נמצאה או חוסר התאמה בנתוני הפקדה",
    schedule: "08:45 ו-15:45",
    throttle: "ללא",
    shabbat: "נשלח תמיד",
  },
  11: {
    name: "ללא תקשורת",
    description: "מסוף שלא תקשר 12+ שעות אבל תקשר ב-36 שעות אחרונות. רק מסופים עם כסף במגירה.",
    schedule: "כל 10 דקות, 10:30–20:00",
    throttle: "4 שעות ברמת חברה",
    shabbat: "מושעה למסופים סגורים בשבת (ממוצע ≤ 2 משיכות)",
  },
  12: {
    name: "ללא משיכות",
    description: "מסוף שלא בוצעה בו משיכה 5+ שעות ויש בו כסף. רגישות משתנה: 5 שעות מ-11:00, 6 שעות מ-13:00, 8 שעות מ-16:00.",
    schedule: "כל 10 דקות, 11:00–20:00",
    throttle: "4 שעות ברמת חברה",
    shabbat: "מושעה למסופים סגורים בשבת. ביום כניסת שבת — לא נשלח אחרי שעת הכניסה.",
  },
  13: {
    name: "אין מספיק כסף במסוף",
    description: "סכום במגירה נמוך מממוצע יומי או פחות מ-2,500 ש\"ח עם משיכות צפויות.",
    schedule: "כל 10 דקות, 10:30–20:00",
    throttle: "6 שעות ברמת חברה",
    shabbat: "מושעה למסופים סגורים בשבת",
  },
  14: {
    name: "כספת פתוחה",
    description: "כספת פתוחה 5+ דקות ללא סוף יום תואם. נשלח פעם אחת, אחריו תזכורות (קוד 18).",
    schedule: "כל 10 דקות, 08:00–20:00",
    throttle: "פעם אחת לאירוע",
    shabbat: "נשלח תמיד",
  },
  15: {
    name: "מסוף חסום — לא מחזיר כסף",
    description: "מסוף שלא הפקיד 10+ ימים.",
    schedule: "פעם ביום ב-13:00",
    throttle: "פעם ביום למסוף",
    shabbat: "לא נשלח בשבת/חג",
  },
  16: {
    name: "אין נתונים מכספונט",
    description: "אין עדכון ממערכת Casponet 20+ דקות.",
    schedule: "כל 10 דקות, 08:00–22:00",
    throttle: "3 שעות לחברה",
    shabbat: "נשלח תמיד בשעות פעילות",
  },
  17: {
    name: "תחילת יום",
    description: "הודעת opt-in לתחילת יום — מאפשרת קבלת הודעות WhatsApp בטקסט חופשי.",
    schedule: "לפי הגדרה",
    throttle: "פעם ביום",
    shabbat: "לא נשלח בשבת",
  },
  18: {
    name: "כספת פתוחה — תזכורת",
    description: "כספת עדיין פתוחה אחרי התראה ראשונה (קוד 14). נשלח כל 3 שעות עד שהכספת נסגרת.",
    schedule: "כל 3 שעות בשעות פעילות",
    throttle: "3 שעות",
    shabbat: "נשלח תמיד",
  },
  19: {
    name: "תקלה חוזרת",
    description: "מסוף עם 3+ תקלות שונות ב-30 יום.",
    schedule: "פעם ביום ב-09:05",
    throttle: "30 ימים למסוף",
    shabbat: "לא נשלח בשבת/חג",
  },
  20: {
    name: "חוסר התאמה בדיווח",
    description: "אצווה 0000 — חוסר התאמה בין רישום באפליקציה לרישום במסוף.",
    schedule: "פעם ביום ב-11:00",
    throttle: "פעם ביום למסוף",
    shabbat: "לא נשלח בשבת/חג",
  },
};

const MessageRoleMapping = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [breadcrumbItems] = useState([
    { title: t("APPLICATION_TITLE"), link: "#" },
    { title: t("ADMIN.TITLE"), link: "#" },
    { title: t("ADMIN.MESSAGE_ROLE_MAPPING"), link: "#" },
  ]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [showHelp, setShowHelp] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState(null);

  useEffect(() => {
    loadMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMappings = async () => {
    setIsLoading(true);
    dispatch(setLoadingOverlay(true));
    try {
      const companyId = LocalstorageService.getItem(CompanyId);
      const response = await NotificationConfigService.getMessageRoleMappings(companyId);
      if (response.data && response.data.status === 200 && response.data.data) {
        setMappings(response.data.data);
      }
    } catch (error) {
      console.error("Error loading message role mappings:", error);
      toast.error(t("ADMIN.LOAD_ERROR"));
    } finally {
      setIsLoading(false);
      dispatch(setLoadingOverlay(false));
    }
  };

  const handleToggle = (messageCode, roleId) => {
    setMappings((prev) =>
      prev.map((mapping) => {
        if (mapping.messageCode !== messageCode) return mapping;
        const hasRole = mapping.roleIds.includes(roleId);
        return {
          ...mapping,
          roleIds: hasRole
            ? mapping.roleIds.filter((id) => id !== roleId)
            : [...mapping.roleIds, roleId],
        };
      })
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    dispatch(setLoadingOverlay(true));
    try {
      const companyId = LocalstorageService.getItem(CompanyId);
      const response = await NotificationConfigService.saveMessageRoleMappings({
        companyId,
        mappings,
      });
      if (response.data && response.data.status === 200) {
        toast.success(t("ADMIN.SAVE_SUCCESS"));
      } else {
        toast.error(response.data?.message || t("ADMIN.SAVE_ERROR"));
      }
    } catch (error) {
      console.error("Error saving message role mappings:", error);
      toast.error(t("ADMIN.SAVE_ERROR"));
    } finally {
      setIsSaving(false);
      dispatch(setLoadingOverlay(false));
    }
  };

  const openMessageInfo = (messageCode) => {
    const info = MESSAGE_INFO[messageCode];
    if (info) {
      setSelectedInfo({ code: messageCode, ...info });
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Breadcrumbs
          title={t("ADMIN.MESSAGE_ROLE_MAPPING")}
          breadcrumbItems={breadcrumbItems}
        />

        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="card-title mb-0">{t("ADMIN.MESSAGE_ROLE_MAPPING")}</h4>
              <Button color="info" outline size="sm" onClick={() => setShowHelp(true)}>
                <i className="mdi mdi-help-circle-outline me-1"></i>
                עזרה
              </Button>
            </div>

            {isLoading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t("ADMIN.MESSAGE_NAME")}</th>
                    {ROLE_COLUMNS.map((role) => (
                      <th key={role.roleId} className="text-center">
                        {t(role.translationKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((mapping, index) => {
                    const info = MESSAGE_INFO[mapping.messageCode];
                    return (
                      <tr key={mapping.messageCode}>
                        <td>{mapping.messageCode}</td>
                        <td>
                          <span
                            id={`msg-info-${mapping.messageCode}`}
                            style={{ cursor: "pointer" }}
                            onClick={() => openMessageInfo(mapping.messageCode)}
                          >
                            {mapping.messageName ||
                              t("MESSAGE_CODES.CODE_" + mapping.messageCode)}
                            {info && (
                              <i className="mdi mdi-information-outline text-info ms-1" style={{ fontSize: "14px" }}></i>
                            )}
                          </span>
                          {info && (
                            <UncontrolledTooltip placement="left" target={`msg-info-${mapping.messageCode}`}>
                              {info.description}
                            </UncontrolledTooltip>
                          )}
                          {mapping.messageCodeDescription && (
                            <small className="text-muted ms-1 d-block">
                              {mapping.messageCodeDescription}
                            </small>
                          )}
                        </td>
                        {ROLE_COLUMNS.map((role) => (
                          <td key={role.roleId} className="text-center">
                            <Input
                              type="checkbox"
                              checked={mapping.roleIds.includes(role.roleId)}
                              onChange={() =>
                                handleToggle(mapping.messageCode, role.roleId)
                              }
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}

            <div className="mt-4">
              <Button
                color="primary"
                onClick={handleSave}
                disabled={isSaving || isLoading}
              >
                {isSaving ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {t("SAVING")}
                  </>
                ) : (
                  t("SAVE_TITLE")
                )}
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* חלון פרטי הודעה */}
      <Modal isOpen={!!selectedInfo} toggle={() => setSelectedInfo(null)} centered>
        {selectedInfo && (
          <>
            <ModalHeader toggle={() => setSelectedInfo(null)}>
              קוד {selectedInfo.code}: {selectedInfo.name}
            </ModalHeader>
            <ModalBody>
              <table className="table table-sm mb-0">
                <tbody>
                  <tr>
                    <td className="fw-bold" style={{ width: "35%" }}>תיאור</td>
                    <td>{selectedInfo.description}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">תזמון</td>
                    <td>{selectedInfo.schedule}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">חוסם כפילויות</td>
                    <td>{selectedInfo.throttle}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">שבת/חג</td>
                    <td>{selectedInfo.shabbat}</td>
                  </tr>
                </tbody>
              </table>
            </ModalBody>
          </>
        )}
      </Modal>

      {/* חלון עזרה כללי */}
      <Modal isOpen={showHelp} toggle={() => setShowHelp(false)} size="lg" centered scrollable>
        <ModalHeader toggle={() => setShowHelp(false)}>
          מדריך התראות והודעות
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            לחצו על שם ההודעה בטבלה או על אייקון <i className="mdi mdi-information-outline text-info"></i> לפרטים נוספים.
          </p>

          <h6 className="mt-3">שעות שקט</h6>
          <table className="table table-sm table-bordered">
            <thead><tr><th>חלון</th><th>שעות</th><th>התנהגות</th></tr></thead>
            <tbody>
              <tr><td>לילה</td><td>22:00–07:30</td><td>מנהלים: מיידי. עובדים: מושהה</td></tr>
              <tr><td>שבת</td><td>לפי הגדרות שבת</td><td>מנהלים: מיידי. עובדים: מושהה</td></tr>
            </tbody>
          </table>

          <h6 className="mt-3">ערוצי משלוח</h6>
          <table className="table table-sm table-bordered">
            <thead><tr><th>ערוץ</th><th>קוד</th><th>התנהגות</th></tr></thead>
            <tbody>
              <tr><td>לא לשלוח</td><td>0</td><td>המשתמש לא מקבל הודעות</td></tr>
              <tr><td>SMS בלבד</td><td>1</td><td>SMS דרך Relay</td></tr>
              <tr><td>WhatsApp בלבד</td><td>2</td><td>opt-in → טקסט חופשי, אחרת תבנית</td></tr>
              <tr><td>שניהם</td><td>3</td><td>SMS + WhatsApp</td></tr>
            </tbody>
          </table>

          <h6 className="mt-3">תפקידים</h6>
          <table className="table table-sm table-bordered">
            <thead><tr><th>תפקיד</th><th>קבלת הודעות</th></tr></thead>
            <tbody>
              <tr><td>מנהל מערכת / מנהל חברה</td><td>כל ההודעות, מיידי תמיד</td></tr>
              <tr><td>סוכן</td><td>לפי שיוך, מושהה בשעות שקט</td></tr>
              <tr><td>עובד</td><td>לפי שיוך מסופים, מושהה בשעות שקט</td></tr>
            </tbody>
          </table>

          <h6 className="mt-3">מנגנון WhatsApp Opt-In</h6>
          <ul className="mb-0">
            <li>כשאין opt-in פעיל — נשלחת הודעת אישור</li>
            <li>אם לא אושר תוך 5 דקות — נשלחת הודעה בתבנית</li>
            <li>עד 2 ניסיונות, אחריו ניסיון הבא אחרי 4 שעות</li>
            <li>Opt-in תקף 24 שעות מרגע האישור</li>
          </ul>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default MessageRoleMapping;
